import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

export const PLAN_LABELS = {
  trial:    "Essai gratuit",
  solo:     "Solo",
  starter:  "Solo",
  studio:   "Studio",
  pro:      "Studio",
  premium:  "Premium",
  expert:   "Premium",
  lifetime: "Lifetime",
};

// Prix en euros
export const PLAN_PRICES = {
  starter:  99,
  pro:      199,
  expert:   399,
  lifetime: 2900,
};

export const PLAN_CURRENCY = "€";

export const PLAN_FEATURES = {
  starter: [
    "3 projets actifs",
    "1 utilisateur",
    "Modules Conception + Déco + Contrats",
    "Questionnaire client",
    "Support email",
  ],
  pro: [
    "10 projets actifs",
    "3 utilisateurs",
    "Tous les modules",
    "Espace entreprises",
    "Messagerie interne",
    "Support prioritaire",
  ],
  expert: [
    "Projets illimités",
    "Utilisateurs illimités",
    "Tout débloqué",
    "Branding personnalisé",
    "Support dédié",
  ],
  lifetime: [
    "Accès à vie, paiement unique",
    "Projets & utilisateurs illimités",
    "Tous les modules débloqués",
    "Toutes les futures fonctionnalités",
    "Branding personnalisé",
    "Support prioritaire à vie",
  ],
};

const PLAN_ORDER = {
  trial: 0,
  solo: 1,
  starter: 2,
  pro: 3,
  expert: 4,
  lifetime: 999,
};

const PLAN_ALIASES = {
  solo: "starter",    // Solo payant → accès niveau Starter (pas Pro/Expert)
  studio: "pro",
  premium: "expert",
};

export function usePlan() {
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState("trial");
  const [subscriptionStatus, setSubscriptionStatus] = useState("inactive");

  useEffect(() => {
    let alive = true;

    async function loadPlan() {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session?.user?.id) {
          if (!alive) return;
          setPlan("trial");
          setSubscriptionStatus("inactive");
          setLoading(false);
          return;
        }

        const userId = session.user.id;

        // IMPORTANT : on lit d'abord sur admin_id, car c'est la clé réellement utilisée dans ton app
        let { data, error } = await supabase
          .from("cabinets")
          .select("plan, subscription_status")
          .eq("admin_id", userId)
          .maybeSingle();

        // Fallback si jamais certains anciens enregistrements utilisent owner_id
        if ((!data || error) && !error) {
          const fallback = await supabase
            .from("cabinets")
            .select("plan, subscription_status")
            .eq("owner_id", userId)
            .maybeSingle();

          data = fallback.data;
          error = fallback.error;
        }

        if (error) {
          console.error("Erreur chargement plan :", error);
        }

        const rawPlan = data?.plan || "trial";
        const normalizedPlan = PLAN_ALIASES[rawPlan] || rawPlan;
        const rawStatus = data?.subscription_status || "inactive";

        if (!alive) return;

        setPlan(normalizedPlan);
        setSubscriptionStatus(rawStatus);
        setLoading(false);
      } catch (err) {
        console.error("Erreur usePlan :", err);
        if (!alive) return;
        setPlan("trial");
        setSubscriptionStatus("inactive");
        setLoading(false);
      }
    }

    loadPlan();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      loadPlan();
    });

    return () => {
      alive = false;
      subscription.unsubscribe();
    };
  }, []);

  function hasAccess(requiredPlan) {
    if (!requiredPlan) return true;

    const currentPlan = PLAN_ALIASES[plan] || plan;
    const neededPlan = PLAN_ALIASES[requiredPlan] || requiredPlan;

    if (currentPlan === "lifetime") return true;

    if (subscriptionStatus !== "active") {
      return neededPlan === "trial";
    }

    const currentLevel = PLAN_ORDER[currentPlan] ?? 0;
    const requiredLevel = PLAN_ORDER[neededPlan] ?? 999;

    return currentLevel >= requiredLevel;
  }

  return {
    hasAccess,
    loading,
    plan,
    subscriptionStatus,
  };
}